import React, { useState, useEffect } from 'react';
import './Heatmap.css';
import { API_BASE_URL as API_BASE } from '../config';
import { extractSectorData } from '../utils/responseHandlers';
import StockLogo from './StockLogo';

const Heatmap = ({ onBack }) => {
  const [selectedSector, setSelectedSector] = useState('Nifty 50');
  const [showAllSectors, setShowAllSectors] = useState(true);
  const [sectorHeatmapData, setSectorHeatmapData] = useState({});
  const [sectorErrors, setSectorErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  const CACHE_KEY = 'heatmap_cache';

  // Must match backend sector_stocks keys exactly
  const sectors = [
    'AUTO',
    'IT',
    'FMCG',
    'REALTY',
    'CEMENT',
    'PSU BANK',
    'METAL',
    'ENERGY',
    'PHARMA',
    'PVT BANK',
    'NIFTY50',
    'BANK NIFTY',
    'NIFTY MID SELECT',
    'FIN SERVICE',
    'INFRA',
    'SENSEX',
    'COUSUMER DURABLES'
  ];

  const formatNumber = (value, decimals = 2) => {
    const num = value !== null && value !== undefined ? Number(value) : NaN;
    if (!Number.isFinite(num)) return null;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const pickFirst = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

  // Sync dark mode with root html tag class
  useEffect(() => {
    const darkMode = document.documentElement.classList.contains('darkmode');
    setIsDarkMode(darkMode);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const isNowDark = document.documentElement.classList.contains('darkmode');
          setIsDarkMode(isNowDark);
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const fetchSectorHeatmap = async (sector) => {
    // Build URL with query parameters
    const url = new URL(`${API_BASE}/sector-abnormal-growth`);
    url.searchParams.append('sector', sector);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    const processed = [];
    const seenSymbols = new Set();

    // Use response handler to extract sector data from multiple formats
    const { stocks: allStocks } = extractSectorData(result);

    if (allStocks && Array.isArray(allStocks)) {
      allStocks.forEach((stock, idx) => {
        if (stock && stock.symbol && !seenSymbols.has(stock.symbol)) {
          const rawChange = stock.avg_growth || stock.change || stock.percentage_change;
          const parsed = rawChange !== null && rawChange !== undefined
            ? parseFloat(rawChange)
            : 0;
          const change = Number.isFinite(parsed) ? parsed : 0;

          const price = pickFirst(stock.price, stock.ltp, stock.LTP, stock.last_price, stock.close, stock.close_price);

          seenSymbols.add(stock.symbol);
          processed.push({
            symbol: stock.symbol,
            name: stock.company_name || stock.symbol,
            change,
            price,
            meta: stock,
            size: idx < 5 ? 'large' : idx < 10 ? 'medium' : 'small'
          });
        }
      });
    }


    if (processed.length === 0) {
      return [];
    }

    const positives = processed
      .filter((s) => s.change >= 0)
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

    const negatives = processed
      .filter((s) => s.change < 0)
      .sort((a, b) => Math.abs(a.change) - Math.abs(b.change));

    const ranked = [...positives, ...negatives].map((stock, idx) => {
      const computedSize = idx < 5 ? 'large' : idx < 10 ? 'medium' : 'small';
      return {
        ...stock,
        rank: idx + 1,
        size: stock.isAbnormal ? 'large' : computedSize
      };
    });

    return ranked;
  };

  // Fetch data when sector changes or refresh is clicked
  useEffect(() => {
    const fetchData = async () => {
      const now = Date.now();
      const cacheExpired = !cacheTimestamp || (now - cacheTimestamp) > CACHE_DURATION;
      
      // Check if we need to fetch (refresh clicked or cache expired)
      if (!shouldRefresh && !cacheExpired && Object.keys(sectorHeatmapData).length > 0) {
        // Use cached data, don't show loading
        return;
      }

      setLoading(true);
      setError('');

      try {
        setSectorErrors({});

        if (showAllSectors) {
          const results = await Promise.allSettled(sectors.map((s) => fetchSectorHeatmap(s)));
          const nextData = {};
          const nextErrors = {};

          results.forEach((res, idx) => {
            const sector = sectors[idx];
            if (res.status === 'fulfilled') {
              nextData[sector] = res.value;
              if (!res.value || res.value.length === 0) {
                nextErrors[sector] = 'No data available for this sector';
              }
            } else {
              nextData[sector] = [];
              nextErrors[sector] = `Failed to load: ${res.reason?.message || 'Unknown error occurred'}`;
            }
          });

          setSectorHeatmapData(nextData);
          setSectorErrors(nextErrors);
          setCacheTimestamp(now);
        } else {
          const data = await fetchSectorHeatmap(selectedSector);
          setSectorHeatmapData({ [selectedSector]: data });
          setSectorErrors({
            ...(data && data.length > 0 ? {} : { [selectedSector]: 'No data available for this sector' })
          });
          setCacheTimestamp(now);
        }
      } catch (err) {
        console.error('Error fetching heatmap data:', err);
        setError(`Failed to load heatmap data: ${err.message || 'Unknown error occurred'}`);
        setSectorHeatmapData({});
        setSectorErrors({});
      } finally {
        setLoading(false);
        setShouldRefresh(false);
      }
    };

    // Add a small delay to avoid rapid API calls when changing filters
    const timer = setTimeout(() => {
      fetchData().catch(console.error);
    }, 300);

    // Cleanup function to cancel any pending requests
    return () => clearTimeout(timer);
  }, [selectedSector, showAllSectors, shouldRefresh]);

  // Color and intensity mapping based on % change buckets
  // <1% lowest color grade
  // 1% to 2% 2nd grade
  // 2% to 2.5% 3rd grade
  // 2.5% to 4% 4th grade
  // 4% and across 5th grade
  const getIntensityClass = (data, stock) => {
    const change = Number.isFinite(stock?.change) ? stock.change : 0;
    const absChange = Math.abs(change);

    if (absChange < 1) return 'g1';
    if (absChange < 2) return 'g2';
    if (absChange < 2.5) return 'g3';
    if (absChange < 4) return 'g4';
    return 'g5';
  };

  const getColorClass = (change) => (change >= 0 ? 'positive' : 'negative');
  const rootClasses = `heatmap-page ${isDarkMode ? 'darkmode' : ''}`;
  const handleSectorSelect = (sector) => {
    setSelectedSector(sector);
    setShowAllSectors(true); // Keep all sectors visible

    setTimeout(() => {
      const element = document.getElementById(`sector-${sector.replace(/\s+/g, '-')}`);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 50);
  };

  const handleRefresh = () => {
    setShouldRefresh(true);
    setShowAllSectors(true);
  };

  const getTooltipRows = (stock) => {
    const meta = stock?.meta || {};

    const rows = [
      { label: 'Change', value: pickFirst(meta.change, meta.day_change, meta.absolute_change) },
      { label: 'VWAP', value: pickFirst(meta.vwap, meta.VWAP) },
      { label: 'High', value: pickFirst(meta.high, meta.day_high) },
      { label: 'Low', value: pickFirst(meta.low, meta.day_low) },
      { label: 'Traded Volume (Lakhs)', value: pickFirst(meta.traded_volume_lakhs, meta.volume_lakhs, meta.volume) },
      { label: 'Traded Value (Cr.)', value: pickFirst(meta.traded_value_cr, meta.value_cr, meta.value) }
    ];

    return rows
      .map((r) => {
        const formatted = typeof r.value === 'number' || (typeof r.value === 'string' && r.value.trim() !== '' && Number.isFinite(Number(r.value)))
          ? formatNumber(r.value, 2)
          : (r.value !== undefined && r.value !== null && r.value !== '' ? String(r.value) : null);
        return { ...r, value: formatted };
      })
      .filter((r) => r.value !== null);
  };

  const renderTile = (data, stock, index) => {
    const priceText = formatNumber(stock.price, 2);
    const changeText = `${stock.change >= 0 ? '+' : '-'}${Math.abs(stock.change).toFixed(2)}%`;
    const tooltipRows = getTooltipRows(stock);

    return (
      <div
        key={stock.symbol}
        className={`heatmap-tile ${stock.size} ${stock.isAbnormal ? 'isAbnormal' : ''} ${getColorClass(stock.change)} ${getIntensityClass(data, stock)}`}
        title={`${stock.name}: ${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}%`}
      >
        <div className="tile-content">
          <div className="tile-top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <StockLogo symbol={stock.symbol} size={16} />
            <div className="tile-symbol div-heatmap">{stock.symbol}</div>
          </div>
          <div className="tile-middle">
            <div className="tile-price div-heatmap">{priceText || '—'}</div>
          </div>
          <div className="tile-bottom">
            <div className="tile-change div-heatmap">{changeText}</div>
          </div>
        </div>

        {tooltipRows.length > 0 ? (
          <div className="heatmap-tooltip-card" role="tooltip">
            <div className="heatmap-tooltip-header">
              <div className="heatmap-tooltip-symbol">{stock.symbol}</div>
              <div className="heatmap-tooltip-tabs">Price | Graph</div>
            </div>
            <div className="heatmap-tooltip-body">
              {tooltipRows.map((row) => (
                <div key={row.label} className="heatmap-tooltip-row">
                  <div className="heatmap-tooltip-label">{row.label}</div>
                  <div className="heatmap-tooltip-value">{row.value}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderGrid = (data) => (
    <div className="heatmap-grid">
      {data.map((stock, index) => renderTile(data, stock, index))}
    </div>
  );

  return (
    <div className={rootClasses}>
      <header className="heatmap-header">
        <div className="heatmap-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
        </div>
      </header>

      
        <div className="heatmap-container">
          <div className="heatmap-title s4u-title-row">
            <div className="s4u-page-heading">
              <button className="heatmap-back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
              <h1>Sector Heatmap</h1>
            </div>
            <div className="heatmap-info">
              <span className="info-icon">ℹ️</span>
              <span className="info-text">Visual market performance analysis</span>
            </div>
          </div>

          {/* Controls */}
          <div className="heatmap-controls">
            <div className="sector-chips" role="tablist" aria-label="Sectors">
              {(showAllSectors ? sectors : [selectedSector]).map((sector) => (
                <button
                  key={sector}
                  type="button"
                  className={`sector-chip ${selectedSector === sector ? 'active' : ''}`}
                  onClick={() => handleSectorSelect(sector)}
                >
                  {sector}
                </button>
              ))}
            </div>

            <div className="sector-dropdown-mobile-container">
              <select 
                className="sector-dropdown-mobile"
                value={selectedSector}
                onChange={(e) => {
                  if (e.target.value === "All Sectors") {
                    handleRefresh();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  } else {
                    handleSectorSelect(e.target.value);
                  }
                }}
              >
                <option value="All Sectors">All Sectors</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            <button type="button" className="heatmap-refresh-btn" onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          {/* Heatmap Grid */}
          {loading ? (
            <div className="heatmap-grid-container">
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div className="heatmap-grid-container">
              <p>{error}</p>
            </div>
          ) : showAllSectors ? (
            <div className="sector-heatmaps">
              {sectors.map((sector) => {
                const data = sectorHeatmapData[sector] || [];
                const sectorError = sectorErrors[sector];

                return (
                  <section key={sector} id={`sector-${sector.replace(/\s+/g, '-')}`} className="sector-heatmap-section">
                    <div className="sector-heatmap-title">{sector}</div>
                    <div className="heatmap-grid-container">
                      {sectorError ? (
                        <p>{sectorError}</p>
                      ) : (
                        renderGrid(data)
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="heatmap-grid-container">
              {sectorErrors[selectedSector] ? (
                <p>{sectorErrors[selectedSector]}</p>
              ) : (
                renderGrid(sectorHeatmapData[selectedSector] || [])
              )}
            </div>
          )}
        </div>
    </div>
  );
};

export default Heatmap;


